import { useHistory } from 'react-router';
import { LOG_OUT, SET_SESSION_ERROR_PENDING } from '../constants';
import { useUserState } from '../hooks/context/useUserState';
import {
  CLIENT_ERROR,
  CONTENT_TOO_LARGE_ERROR,
  ENTRY_ERROR,
  FORCE_ERROR,
  SERVER_ERROR,
  SESSION_ERROR,
} from '../constants';
import { useStart } from '../api/hooks/useStart';
import toast from 'react-hot-toast';
import { translatedToast } from '../utils/helpers';
import useRoutes from './useRoutes';
import useLocalStorageKey from './useLocalStorageKey';
import { useForceErrorModal } from '../context/ForceErrorModalContext';
import { useEntryErrorModalContext } from './context/useEntryErrorModalContext';
import { getClientErrorToastId } from '../api/utils/getClientErrorToastId';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('forceErrors');

export type ForceModalActionFn = (forceValue: string) => any;

const useApiErrorHandling = () => {
  const { dispatch } = useUserState();
  const history = useHistory();
  const { start } = useStart();
  const routes = useRoutes();
  const localStorageKey = useLocalStorageKey();
  const { openForceErrorModal } = useForceErrorModal();
  const { openEntryErrorModal } = useEntryErrorModalContext();

  const startSession = async () => {
    try {
      await start();
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleApiError = async (
    error,
    disableToasts = false,
    forceModalAction?: ForceModalActionFn | undefined,
    // Optional callback invoked when user cancels the force error modal
    forceErrorCancelAction?: () => void
  ) => {
    switch (error.name) {
      case SERVER_ERROR:
        if (!disableToasts) {
          translatedToast('toasts.serverUnavailable');
        }
        break;
      case CONTENT_TOO_LARGE_ERROR:
        if (!disableToasts) {
          translatedToast('toasts.contentTooLargeError');
        }
        break;
      case CLIENT_ERROR:
        if (!disableToasts) {
          const toastId = getClientErrorToastId(error.code);
          if (toastId) {
            translatedToast(toastId);
          } else {
            toast(error.message);
          }
        }
        break;
      case SESSION_ERROR:
        // useUserState doesn't update fast enough, need to read and update local storage directly
        let sessionError = false;
        const storedUserData = window.localStorage.getItem(localStorageKey);
        let userData;
        if (storedUserData) {
          userData = JSON.parse(storedUserData);
          sessionError = userData.sessionErrorPending;
        }
        if (!sessionError) {
          if (userData) {
            localStorage.setItem(
              localStorageKey,
              JSON.stringify({ ...userData, sessionErrorPending: true })
            );
          }
          dispatch({ type: SET_SESSION_ERROR_PENDING, payload: true });
          translatedToast('toasts.sessionUnavailable');
          dispatch({ type: LOG_OUT });
          history.push(routes.home());
          startSession();
        }
        break;
      case FORCE_ERROR:
        const forceError = JSON.parse(error.message);
        if (forceModalAction) {
          /** @debugFeature forceErrors Logs a force error routed to its modal, and whether a cancel action was supplied. */
          log('force error routed to modal', () => ({
            message: forceError.message,
            hasCancelAction: Boolean(forceErrorCancelAction),
          }));

          const forceRes = await openForceErrorModal(
            forceError.previousForceHeader,
            forceError.message,
            forceError.answers,
            forceModalAction,
            forceErrorCancelAction
          );
          return forceRes;
        }

        /** @debugFeature forceErrors Logs a force error dropped because the caller supplied no forceModalAction — no modal and no toast appear. */
        log('force error dropped, no forceModalAction', () => ({
          message: forceError.message,
        }));
        break;
      case ENTRY_ERROR:
        const entryError = JSON.parse(error.message);
        if (forceModalAction) {
          /** @debugFeature forceErrors Logs an entry error routed to its modal, and whether a cancel action was supplied. */
          log('entry error routed to modal', () => ({
            message: entryError.message,
            entryForce: entryError.entry?.force,
            hasCancelAction: Boolean(forceErrorCancelAction),
          }));

          const entryRes = await openEntryErrorModal(
            entryError.previousForce,
            entryError.message,
            entryError.entry,
            forceModalAction,
            forceErrorCancelAction
          );
          return entryRes;
        }

        /** @debugFeature forceErrors Logs an entry error dropped because the caller supplied no forceModalAction — no modal and no toast appear. */
        log('entry error dropped, no forceModalAction', () => ({
          message: entryError.message,
        }));
        break;
      default:
        if (!disableToasts) {
          toast(error.message);
        }
        break;
    }
    return error;
  };
  return handleApiError;
};

export default useApiErrorHandling;
